import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  Instagram, 
  Facebook, 
  Youtube,
  Twitter,
  User,
  Globe,
  Clock,
  Hash,
  Link,
  CheckCircle,
  XCircle,
  ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('connections');
  const [connections, setConnections] = useState({
    youtube: { connected: false },
    instagram: { connected: false },
    tiktok: { connected: false }
  });
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  
  const [settings, setSettings] = useState({
    social_media: {
      instagram_username: '',
      instagram_profile_url: '',
      instagram_default_hashtags: '',
      facebook_page_name: '',
      facebook_page_url: '',
      facebook_default_hashtags: '',
      youtube_channel_name: '',
      youtube_channel_url: '',
      tiktok_username: '',
      tiktok_profile_url: '',
      twitter_username: '',
      twitter_profile_url: '',
      default_posting_time: '',
      auto_hashtags: true
    },
    profile: {
      name: '',
      bio: '',
      website: '',
      timezone: ''
    }
  });

  useEffect(() => {
    fetchSettings();
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const response = await axios.get(`${API}/api/social/connections`, {
        withCredentials: true
      });
      setConnections(response.data);
    } catch (error) {
      console.error('Failed to fetch connections:', error);
    }
  };

  const connectPlatform = async (platform) => {
    setConnectingPlatform(platform);
    try {
      const response = await axios.get(`${API}/api/${platform}/auth-url`, {
        withCredentials: true
      });
      if (response.data.auth_url) {
        // Open in new window for OAuth
        window.open(response.data.auth_url, '_blank', 'width=600,height=700');
        toast.info(`Complete the ${platform} authorization in the popup window`);
      }
    } catch (error) {
      console.error(`Failed to get ${platform} auth URL:`, error);
      toast.error(`Failed to connect to ${platform}`);
    } finally {
      setConnectingPlatform(null);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/settings`, {
        withCredentials: true
      });
      setSettings({
        social_media: response.data.social_media || {},
        profile: response.data.profile || {}
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      social_media: {
        ...prev.social_media,
        [field]: value
      }
    }));
  };

  const handleProfileChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      profile: {
        ...prev.profile,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings`, settings, {
        withCredentials: true
      });
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  const tabs = [
    { id: 'connections', label: 'Connected Accounts', icon: Link },
    { id: 'social', label: 'Social Media', icon: Globe },
    { id: 'profile', label: 'Profile', icon: User }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-zinc-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Settings</h1>
              <p className="text-zinc-400 text-sm">Manage your account and social media settings</p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-violet-600 hover:bg-violet-500"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-zinc-800 pb-4">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id 
                ? 'bg-violet-600 hover:bg-violet-500' 
                : 'text-zinc-400 hover:text-white'
              }
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Connected Accounts Tab */}
        {activeTab === 'connections' && (
          <div className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-2">Connect Your Social Media</h3>
              <p className="text-zinc-400 text-sm mb-6">
                Connect your accounts to enable direct posting from Saketh Studio
              </p>
              
              <div className="space-y-4">
                {/* YouTube */}
                <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-red-600 flex items-center justify-center">
                      <Youtube className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">YouTube</h4>
                      <p className="text-sm text-zinc-400">Upload videos & shorts directly</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {connections.youtube?.connected ? (
                      <>
                        <span className="flex items-center gap-1 text-green-400 text-sm">
                          <CheckCircle className="w-4 h-4" /> Connected
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => connectPlatform('youtube')}
                          className="border-zinc-600 text-zinc-300"
                        >
                          Reconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => connectPlatform('youtube')}
                        disabled={connectingPlatform === 'youtube'}
                        className="bg-red-600 hover:bg-red-500"
                        data-testid="connect-youtube-btn"
                      >
                        {connectingPlatform === 'youtube' ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <ExternalLink className="w-4 h-4 mr-2" />
                        )}
                        Connect
                      </Button>
                    )}
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center">
                      <Instagram className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">Instagram</h4>
                      <p className="text-sm text-zinc-400">Post images & reels directly</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {connections.instagram?.connected ? (
                      <>
                        <span className="flex items-center gap-1 text-green-400 text-sm">
                          <CheckCircle className="w-4 h-4" /> Connected
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => connectPlatform('instagram')}
                          className="border-zinc-600 text-zinc-300"
                        >
                          Reconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => connectPlatform('instagram')}
                        disabled={connectingPlatform === 'instagram'}
                        className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90"
                        data-testid="connect-instagram-btn"
                      >
                        {connectingPlatform === 'instagram' ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <ExternalLink className="w-4 h-4 mr-2" />
                        )}
                        Connect
                      </Button>
                    )}
                  </div>
                </div>

                {/* TikTok */}
                <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-black border border-zinc-700 flex items-center justify-center">
                      <TikTokIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">TikTok</h4>
                      <p className="text-sm text-zinc-400">Upload videos to your inbox</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {connections.tiktok?.connected ? (
                      <>
                        <span className="flex items-center gap-1 text-green-400 text-sm">
                          <CheckCircle className="w-4 h-4" /> Connected
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => connectPlatform('tiktok')}
                          className="border-zinc-600 text-zinc-300"
                        >
                          Reconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => connectPlatform('tiktok')}
                        disabled={connectingPlatform === 'tiktok'}
                        className="bg-black border border-zinc-600 hover:bg-zinc-900"
                        data-testid="connect-tiktok-btn"
                      >
                        {connectingPlatform === 'tiktok' ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <ExternalLink className="w-4 h-4 mr-2" />
                        )}
                        Connect
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Setup Instructions */}
              <div className="mt-6 p-4 bg-zinc-800/30 rounded-lg border border-zinc-700">
                <h4 className="font-medium text-white mb-2">Setup Instructions</h4>
                <ul className="text-sm text-zinc-400 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-violet-400 font-bold">•</span>
                    <span><strong>YouTube:</strong> Requires a Google account with YouTube channel access</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-400 font-bold">•</span>
                    <span><strong>Instagram:</strong> Requires Instagram Business/Creator account connected to Facebook Page</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-400 font-bold">•</span>
                    <span><strong>TikTok:</strong> Requires TikTok Creator/Business account</span>
                  </li>
                </ul>
              </div>
            </Card>
          </div>
        )}

        {/* Social Media Settings */}
        {activeTab === 'social' && (
          <div className="space-y-6">
            {/* Instagram */}
            <Card className="bg-zinc-900 border-zinc-800 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center">
                  <Instagram className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Instagram</h3>
                  <p className="text-sm text-zinc-400">Connect your Instagram account</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Username</Label>
                  <Input
                    placeholder="@yourusername"
                    value={settings.social_media.instagram_username || ''}
                    onChange={(e) => handleSocialChange('instagram_username', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="instagram-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Profile URL</Label>
                  <Input
                    placeholder="https://instagram.com/yourusername"
                    value={settings.social_media.instagram_profile_url || ''}
                    onChange={(e) => handleSocialChange('instagram_profile_url', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="instagram-url"
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label className="text-zinc-300">Default Hashtags</Label>
                  <Input
                    placeholder="#marketing #business #startup"
                    value={settings.social_media.instagram_default_hashtags || ''}
                    onChange={(e) => handleSocialChange('instagram_default_hashtags', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="instagram-hashtags"
                  />
                  <p className="text-xs text-zinc-500">These hashtags will be suggested when posting to Instagram</p>
                </div>
              </div>
            </Card>

            {/* Facebook */}
            <Card className="bg-zinc-900 border-zinc-800 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                  <Facebook className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Facebook</h3>
                  <p className="text-sm text-zinc-400">Connect your Facebook page</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Page Name</Label>
                  <Input
                    placeholder="Your Page Name"
                    value={settings.social_media.facebook_page_name || ''}
                    onChange={(e) => handleSocialChange('facebook_page_name', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="facebook-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Page URL</Label>
                  <Input
                    placeholder="https://facebook.com/yourpage"
                    value={settings.social_media.facebook_page_url || ''}
                    onChange={(e) => handleSocialChange('facebook_page_url', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="facebook-url"
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label className="text-zinc-300">Default Hashtags</Label>
                  <Input
                    placeholder="#marketing #business #startup"
                    value={settings.social_media.facebook_default_hashtags || ''}
                    onChange={(e) => handleSocialChange('facebook_default_hashtags', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="facebook-hashtags"
                  />
                </div>
              </div>
            </Card>

            {/* YouTube */}
            <Card className="bg-zinc-900 border-zinc-800 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center">
                  <Youtube className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">YouTube</h3>
                  <p className="text-sm text-zinc-400">Connect your YouTube channel</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Channel Name</Label>
                  <Input
                    placeholder="Your Channel Name"
                    value={settings.social_media.youtube_channel_name || ''}
                    onChange={(e) => handleSocialChange('youtube_channel_name', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="youtube-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Channel URL</Label>
                  <Input
                    placeholder="https://youtube.com/@yourchannel"
                    value={settings.social_media.youtube_channel_url || ''}
                    onChange={(e) => handleSocialChange('youtube_channel_url', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="youtube-url"
                  />
                </div>
              </div>
            </Card>

            {/* Twitter/X */}
            <Card className="bg-zinc-900 border-zinc-800 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center">
                  <Twitter className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Twitter / X</h3>
                  <p className="text-sm text-zinc-400">Connect your Twitter account</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Username</Label>
                  <Input
                    placeholder="@yourusername"
                    value={settings.social_media.twitter_username || ''}
                    onChange={(e) => handleSocialChange('twitter_username', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="twitter-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Profile URL</Label>
                  <Input
                    placeholder="https://x.com/yourusername"
                    value={settings.social_media.twitter_profile_url || ''}
                    onChange={(e) => handleSocialChange('twitter_profile_url', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="twitter-url"
                  />
                </div>
              </div>
            </Card>

            {/* TikTok */}
            <Card className="bg-zinc-900 border-zinc-800 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center border border-zinc-700">
                  <span className="text-white font-bold text-sm">TT</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">TikTok</h3>
                  <p className="text-sm text-zinc-400">Connect your TikTok account</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Username</Label>
                  <Input
                    placeholder="@yourusername"
                    value={settings.social_media.tiktok_username || ''}
                    onChange={(e) => handleSocialChange('tiktok_username', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="tiktok-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Profile URL</Label>
                  <Input
                    placeholder="https://tiktok.com/@yourusername"
                    value={settings.social_media.tiktok_profile_url || ''}
                    onChange={(e) => handleSocialChange('tiktok_profile_url', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="tiktok-url"
                  />
                </div>
              </div>
            </Card>

            {/* Posting Preferences */}
            <Card className="bg-zinc-900 border-zinc-800 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Posting Preferences</h3>
                  <p className="text-sm text-zinc-400">Configure your default posting settings</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Default Posting Time</Label>
                  <Input
                    type="time"
                    value={settings.social_media.default_posting_time || ''}
                    onChange={(e) => handleSocialChange('default_posting_time', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white w-48"
                    data-testid="posting-time"
                  />
                  <p className="text-xs text-zinc-500">Suggested time for scheduling posts</p>
                </div>
                <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Hash className="w-5 h-5 text-violet-400" />
                    <div>
                      <p className="text-white font-medium">Auto-generate Hashtags</p>
                      <p className="text-sm text-zinc-400">Automatically suggest hashtags for your content</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.social_media.auto_hashtags ?? true}
                    onCheckedChange={(checked) => handleSocialChange('auto_hashtags', checked)}
                    data-testid="auto-hashtags-switch"
                  />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Profile Settings */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Profile Information</h3>
                  <p className="text-sm text-zinc-400">Update your personal information</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Display Name</Label>
                  <Input
                    placeholder="Your Name"
                    value={settings.profile.name || ''}
                    onChange={(e) => handleProfileChange('name', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="profile-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Bio</Label>
                  <textarea
                    placeholder="Tell us about yourself..."
                    value={settings.profile.bio || ''}
                    onChange={(e) => handleProfileChange('bio', e.target.value)}
                    className="w-full h-24 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                    data-testid="profile-bio"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Website</Label>
                  <Input
                    placeholder="https://yourwebsite.com"
                    value={settings.profile.website || ''}
                    onChange={(e) => handleProfileChange('website', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="profile-website"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Timezone</Label>
                  <select
                    value={settings.profile.timezone || ''}
                    onChange={(e) => handleProfileChange('timezone', e.target.value)}
                    className="w-full h-10 px-3 bg-zinc-800 border border-zinc-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                    data-testid="profile-timezone"
                  >
                    <option value="">Select timezone</option>
                    <option value="Asia/Kolkata">India (IST)</option>
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="Europe/London">London (GMT)</option>
                    <option value="Europe/Paris">Paris (CET)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                    <option value="Asia/Singapore">Singapore (SGT)</option>
                    <option value="Australia/Sydney">Sydney (AEST)</option>
                  </select>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Save Button (Mobile) */}
        <div className="mt-8 md:hidden">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-violet-600 hover:bg-violet-500 h-12"
            data-testid="save-settings-mobile-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
